/* ═══════════════════════════════════════════════════════
   Search Modal JS — Smith's Blades
   Predictive search with client-side fuzzy matching
   to catch mid-word results the API misses.
   ═══════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ——— CONFIG ——— */
  var DEBOUNCE_MS      = 300;
  var MIN_QUERY_LEN    = 2;
  var MAX_RECENT       = 6;
  var STORAGE_KEY      = 'sb_recent_searches';
  var PRODUCT_LIMIT    = 20;
  var DISPLAY_LIMIT    = 6;   /* How many to show in modal */
  var COLLECTION_LIMIT = 4;
  var PAGE_LIMIT       = 3;

  /* ——— ALL PRODUCTS CACHE ——— */
  /* We fetch all products once and cache them for client-side filtering */
  var allProductsCache  = null;
  var cacheLoading      = false;
  var cacheCallbacks    = [];

  /* ——— DOM REFS ——— */
  var overlay      = document.getElementById('searchOverlay');
  var modal        = document.getElementById('searchModal');
  var input        = document.getElementById('searchInput');
  var clearBtn     = document.getElementById('searchClear');
  var closeBtn     = document.getElementById('searchClose');

  var recentWrap   = document.getElementById('searchRecent');
  var recentList   = document.getElementById('searchRecentList');
  var recentClear  = document.getElementById('searchRecentClear');

  var trendingWrap = document.getElementById('searchTrending');

  var loadingWrap  = document.getElementById('searchLoading');
  var resultsWrap  = document.getElementById('searchResults');
  var emptyWrap    = document.getElementById('searchEmpty');
  var errorWrap    = document.getElementById('searchError');
  var emptyTerm    = document.getElementById('searchEmptyTerm');
  var retryBtn     = document.getElementById('searchRetryBtn');

  var productsSection  = document.getElementById('searchProductsSection');
  var productsGrid     = document.getElementById('searchProductsGrid');
  var productCount     = document.getElementById('searchProductCount');
  var viewProductsLink = document.getElementById('searchViewProducts');

  var collectionsSection = document.getElementById('searchCollectionsSection');
  var collectionsList    = document.getElementById('searchCollectionsList');

  var pagesSection   = document.getElementById('searchPagesSection');
  var pagesList      = document.getElementById('searchPagesList');

  var viewAllWrap    = document.getElementById('searchViewAll');
  var viewAllBtn     = document.getElementById('searchViewAllBtn');

  /* ——— STATE ——— */
  var isOpen        = false;
  var currentQuery  = '';
  var debounceTimer = null;
  var currentXHR    = null;
  var lastFocusEl   = null;

  /* ═══════════════════════════════════════════════════
     HELPERS
     ═══════════════════════════════════════════════════ */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  function getRecent() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
    catch (e) { return []; }
  }

  function saveRecent(term) {
    if (!term || term.length < MIN_QUERY_LEN) return;
    var list = getRecent().filter(function (t) {
      return t.toLowerCase() !== term.toLowerCase();
    });
    list.unshift(term);
    if (list.length > MAX_RECENT) list = list.slice(0, MAX_RECENT);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch (e) {}
  }

  function removeRecent(term) {
    var list = getRecent().filter(function (t) {
      return t.toLowerCase() !== term.toLowerCase();
    });
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch (e) {}
  }

  function clearAllRecent() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  function formatMoney(cents) {
    if (!cents && cents !== 0) return '';
    var format = window.SmithsBlades && window.SmithsBlades.moneyFormat
      ? window.SmithsBlades.moneyFormat
      : '${{amount}}';
    var val = (cents / 100).toFixed(2);
    return format.replace('{{amount}}', val)
                 .replace('{{amount_no_decimals}}', Math.round(cents / 100));
  }

  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;')
              .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ═══════════════════════════════════════════════════
     CLIENT-SIDE FUZZY MATCHING
     Checks if ALL words in query appear ANYWHERE in text
     e.g. "kha" matches "Tutankhamun" because "kha" is in it
     ═══════════════════════════════════════════════════ */
  function containsQuery(text, query) {
    if (!text || !query) return false;
    return text.toLowerCase().indexOf(query.toLowerCase()) !== -1;
  }

  function productMatchesQuery(product, query) {
    var q = query.toLowerCase().trim();
    if (!q) return false;

    /* Build a big searchable string from all product fields */
    var searchableFields = [
      product.title             || '',
      product.vendor            || '',
      product.product_type      || '',
      product.type              || '',
      product.handle            || '',
      (product.tags || []).join(' '),
      (product.variants || []).map(function(v) {
        return (v.title || '') + ' ' + (v.sku || '');
      }).join(' ')
    ];

    var searchableText = searchableFields.join(' ').toLowerCase();

    /* Split query into words — ALL must be present somewhere */
    var words = q.split(/\s+/).filter(function(w) { return w.length > 0; });

    return words.every(function(word) {
      return searchableText.indexOf(word) !== -1;
    });
  }

  /* ═══════════════════════════════════════════════════
     ALL PRODUCTS CACHE
     Fetches all products once for client-side search.
     Uses /collections/all so it gets every product.
     ═══════════════════════════════════════════════════ */
  function getAllProducts(callback) {
    /* Already cached — return immediately */
    if (allProductsCache !== null) {
      callback(allProductsCache);
      return;
    }

    /* Currently loading — queue the callback */
    if (cacheLoading) {
      cacheCallbacks.push(callback);
      return;
    }

    /* Start loading */
    cacheLoading = true;
    cacheCallbacks.push(callback);

    /* Fetch up to 250 products from all collection */
    var url = '/collections/all/products.json?limit=250&sort_by=best-selling';

    var xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.setRequestHeader('Accept', 'application/json');

    xhr.onload = function() {
      cacheLoading = false;
      if (xhr.status >= 200 && xhr.status < 400) {
        try {
          var resp = JSON.parse(xhr.responseText);
          allProductsCache = resp.products || [];
        } catch(e) {
          allProductsCache = [];
        }
      } else {
        allProductsCache = [];
      }

      /* Fire all queued callbacks */
      var callbacks = cacheCallbacks.slice();
      cacheCallbacks = [];
      callbacks.forEach(function(cb) { cb(allProductsCache); });
    };

    xhr.onerror = function() {
      cacheLoading = false;
      allProductsCache = [];
      var callbacks = cacheCallbacks.slice();
      cacheCallbacks = [];
      callbacks.forEach(function(cb) { cb([]); });
    };

    xhr.send();
  }

  /* ═══════════════════════════════════════════════════
     CLIENT-SIDE SEARCH ON CACHED PRODUCTS
     ═══════════════════════════════════════════════════ */
  function searchInCache(query) {
    if (!allProductsCache || !query) return [];

    return allProductsCache.filter(function(product) {
      return productMatchesQuery(product, query);
    });
  }

  /* Convert /collections/all product format to predictive API format */
  function normalizeCachedProduct(p) {
    return {
      id:             p.id,
      title:          p.title,
      handle:         p.handle,
      url:            '/products/' + p.handle,
      vendor:         p.vendor || '',
      price:          p.price || (p.variants && p.variants[0] ? p.variants[0].price : 0),
      compare_at_price: p.compare_at_price || (p.variants && p.variants[0] ? p.variants[0].compare_at_price : 0),
      featured_image: p.images && p.images.length > 0
        ? { url: p.images[0].src, alt: p.images[0].alt || p.title }
        : null
    };
  }

  /* ═══════════════════════════════════════════════════
     OPEN / CLOSE
     ═══════════════════════════════════════════════════ */
  function openModal() {
    if (isOpen) return;
    lastFocusEl = document.activeElement;
    isOpen = true;
    overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
    showDefaultState();

    /* Pre-warm the product cache when modal opens */
    getAllProducts(function() {});

    setTimeout(function () {
      if (input) input.focus();
    }, 100);
    document.addEventListener('keydown', onKeydown);
  }

  function closeModal() {
    if (!isOpen) return;
    isOpen = false;
    overlay.classList.remove('active');
    document.body.style.overflow = '';
    if (currentXHR) { currentXHR.abort(); currentXHR = null; }
    clearTimeout(debounceTimer);
    document.removeEventListener('keydown', onKeydown);
    if (lastFocusEl) {
      try { lastFocusEl.focus(); } catch (e) {}
    }
  }

  /* ═══════════════════════════════════════════════════
     UI STATE
     ═══════════════════════════════════════════════════ */
  function hideAllStates() {
    recentWrap.style.display   = 'none';
    trendingWrap.style.display = 'none';
    loadingWrap.style.display  = 'none';
    resultsWrap.style.display  = 'none';
    emptyWrap.style.display    = 'none';
    errorWrap.style.display    = 'none';
  }

  function showDefaultState() {
    hideAllStates();
    var recent = getRecent();
    if (recent.length > 0) {
      renderRecent(recent);
      recentWrap.style.display = 'block';
    }
    trendingWrap.style.display = 'block';
    input.value = '';
    clearBtn.style.display = 'none';
  }

  function showLoading() {
    hideAllStates();
    loadingWrap.style.display = 'flex';
  }

  function showResults(data) {
    hideAllStates();
    resultsWrap.style.display = 'block';

    var hasProducts    = data.products && data.products.length > 0;
    var hasCollections = data.collections && data.collections.length > 0;
    var hasPages       = data.pages && data.pages.length > 0;
    var hasAnything    = hasProducts || hasCollections || hasPages;

    if (!hasAnything) {
      showEmpty(currentQuery);
      return;
    }

    /* Products */
    if (hasProducts) {
      productsSection.style.display = 'block';
      var displayProducts = data.products.slice(0, DISPLAY_LIMIT);
      productCount.textContent = '(' + data.products.length + ')';
      productsGrid.innerHTML = renderProducts(displayProducts);
      viewProductsLink.href = '/search?q=' + encodeURIComponent(currentQuery) + '&type=product';
    } else {
      productsSection.style.display = 'none';
    }

    /* Collections */
    if (hasCollections) {
      collectionsSection.style.display = 'block';
      collectionsList.innerHTML = renderCollections(data.collections);
    } else {
      collectionsSection.style.display = 'none';
    }

    /* Pages */
    if (hasPages) {
      pagesSection.style.display = 'block';
      pagesList.innerHTML = renderPages(data.pages);
    } else {
      pagesSection.style.display = 'none';
    }

    /* View All */
    viewAllWrap.style.display = 'block';
    viewAllBtn.href = '/search?q=' + encodeURIComponent(currentQuery);
  }

  function showEmpty(query) {
    hideAllStates();
    emptyWrap.style.display = 'flex';
    emptyTerm.textContent = query;
  }

  function showError() {
    hideAllStates();
    errorWrap.style.display = 'flex';
  }

  /* ═══════════════════════════════════════════════════
     RENDER FUNCTIONS
     ═══════════════════════════════════════════════════ */
  function renderProducts(products) {
    return products.map(function (p) {
      var imgSrc = p.featured_image ? p.featured_image.url : '';
      var imgAlt = p.featured_image ? p.featured_image.alt : p.title;

      var priceHtml = '';
      if (p.price) {
        priceHtml = '<span class="search-product-price">' + formatMoney(p.price) + '</span>';
        if (p.compare_at_price && p.compare_at_price > p.price) {
          priceHtml += '<span class="search-product-price-compare">' + formatMoney(p.compare_at_price) + '</span>';
        }
      }

      var vendorHtml = p.vendor
        ? '<p class="search-product-category">' + escapeHtml(p.vendor) + '</p>'
        : '';

      return '' +
        '<a href="' + p.url + '" class="search-product-card" role="option" data-search-result>' +
          '<div class="search-product-image">' +
            (imgSrc
              ? '<img src="' + imgSrc + '&width=300" alt="' + escapeHtml(imgAlt) + '" loading="lazy" width="300" height="400">'
              : '<div class="search-product-image-placeholder"><svg width="60" height="60" viewBox="0 0 60 60" fill="none"><rect x="15" y="5" width="30" height="50" rx="2" fill="#333" opacity="0.3"/></svg></div>'
            ) +
          '</div>' +
          '<div class="search-product-info">' +
            vendorHtml +
            '<h4 class="search-product-title">' + escapeHtml(p.title) + '</h4>' +
            priceHtml +
          '</div>' +
        '</a>';
    }).join('');
  }

  function renderCollections(collections) {
    return collections.map(function (c) {
      return '' +
        '<a href="' + c.url + '" class="search-collection-item" role="option" data-search-result>' +
          '<span class="search-collection-name">' + escapeHtml(c.title) + '</span>' +
          '<span class="search-collection-arrow">›</span>' +
        '</a>';
    }).join('');
  }

  function renderPages(pages) {
    return pages.map(function (p) {
      return '' +
        '<a href="' + p.url + '" class="search-page-item" role="option" data-search-result>' +
          '<span class="search-page-title">' + escapeHtml(p.title) + '</span>' +
          '<span class="search-page-arrow">›</span>' +
        '</a>';
    }).join('');
  }

  function renderRecent(recent) {
    recentList.innerHTML = recent.map(function (term) {
      return '' +
        '<div class="search-recent-item" data-recent-term="' + escapeHtml(term) + '">' +
          '<a href="/search?q=' + encodeURIComponent(term) + '" class="search-recent-item-left">' +
            '<i class="ti ti-clock"></i>' +
            '<span class="search-recent-item-term">' + escapeHtml(term) + '</span>' +
          '</a>' +
          '<button class="search-recent-item-remove" aria-label="Remove ' + escapeHtml(term) + '" data-remove-recent="' + escapeHtml(term) + '">' +
            '<i class="ti ti-x"></i>' +
          '</button>' +
        '</div>';
    }).join('');
  }

  /* ═══════════════════════════════════════════════════
     MERGE + DEDUPLICATE RESULTS
     Combines API results with client-side cache results
     ═══════════════════════════════════════════════════ */
  function mergeProducts(apiProducts, cacheProducts) {
    /* Start with API results (they have full data) */
    var merged = apiProducts.slice();
    var seenIds = {};

    apiProducts.forEach(function(p) {
      seenIds[p.id] = true;
    });

    /* Add cache results that API missed */
    cacheProducts.forEach(function(p) {
      var normalized = normalizeCachedProduct(p);
      if (!seenIds[normalized.id]) {
        seenIds[normalized.id] = true;
        merged.push(normalized);
      }
    });

    return merged;
  }

  /* ═══════════════════════════════════════════════════
     MAIN SEARCH FUNCTION
     1. Show loading
     2. Call Predictive API (fast, catches prefix matches)
     3. Search client-side cache (catches mid-word matches)
     4. Merge both results and display
     ═══════════════════════════════════════════════════ */
  function doSearch(query) {
    if (currentXHR) { currentXHR.abort(); currentXHR = null; }

    currentQuery = query;

    if (!query || query.length < MIN_QUERY_LEN) {
      hideAllStates();
      var recent = getRecent();
      if (recent.length > 0) {
        renderRecent(recent);
        recentWrap.style.display = 'block';
      }
      trendingWrap.style.display = 'block';
      return;
    }

    showLoading();

    /* Track how many sources have responded */
    var apiDone    = false;
    var cacheDone  = false;
    var apiResult  = { products: [], collections: [], pages: [] };
    var cacheResult = [];

    /* ——— Merge and display when BOTH are ready ——— */
    function tryFinish() {
      if (!apiDone || !cacheDone) return;

      var mergedProducts = mergeProducts(apiResult.products, cacheResult);

      showResults({
        products:    mergedProducts,
        collections: apiResult.collections,
        pages:       apiResult.pages
      });
    }

    /* ——— SOURCE 1: Predictive Search API ——— */
    var params = [
      'q=' + encodeURIComponent(query),
      'resources[type]=product,collection,page',
      'resources[limit]=' + PRODUCT_LIMIT,
      'resources[options][unavailable_products]=last',
      'resources[options][fields]=title,product_type,variants.title,vendor,tag,body'
    ].join('&');

    var xhr = new XMLHttpRequest();
    currentXHR = xhr;
    xhr.open('GET', '/search/suggest.json?' + params, true);
    xhr.setRequestHeader('Accept', 'application/json');

    xhr.onload = function () {
      currentXHR = null;
      if (xhr.status >= 200 && xhr.status < 400) {
        try {
          var resp = JSON.parse(xhr.responseText);
          var results = resp.resources && resp.resources.results ? resp.resources.results : {};
          apiResult.products    = results.products    || [];
          apiResult.collections = results.collections || [];
          apiResult.pages       = results.pages       || [];
        } catch (e) {
          /* API failed — cache results will still show */
        }
      }
      apiDone = true;
      tryFinish();
    };

    xhr.onerror = function () {
      currentXHR = null;
      apiDone = true;
      tryFinish();
    };

    xhr.ontimeout = function () {
      currentXHR = null;
      apiDone = true;
      tryFinish();
    };

    xhr.timeout = 8000;
    xhr.send();

    /* ——— SOURCE 2: Client-side cache search ——— */
    getAllProducts(function(products) {
      cacheResult = products.filter(function(p) {
        return productMatchesQuery(p, query);
      });
      cacheDone = true;
      tryFinish();
    });
  }

  /* ═══════════════════════════════════════════════════
     EVENT HANDLERS
     ═══════════════════════════════════════════════════ */
  function onInputChange() {
    var val = input.value.trim();
    clearBtn.style.display = val ? 'flex' : 'none';

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      doSearch(val);
    }, DEBOUNCE_MS);
  }

  function onClearClick() {
    input.value = '';
    clearBtn.style.display = 'none';
    currentQuery = '';
    showDefaultState();
    input.focus();
  }

  function onOverlayClick(e) {
    if (e.target === overlay) closeModal();
  }

  function onKeydown(e) {
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        closeModal();
        break;
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault();
        navigateResults(e.key === 'ArrowDown' ? 1 : -1);
        break;
      case 'Enter':
        onEnterKey(e);
        break;
    }
  }

  /* ——— KEYBOARD NAVIGATION ——— */
  function navigateResults(direction) {
    var items = $$('[data-search-result]', resultsWrap);
    if (items.length === 0) return;

    var current = document.activeElement;
    var index   = items.indexOf(current);
    var next;

    if (index === -1) {
      next = direction > 0 ? 0 : items.length - 1;
    } else {
      next = index + direction;
      if (next >= items.length) next = 0;
      if (next < 0) next = items.length - 1;
    }

    items[next].focus();
  }

  function onEnterKey(e) {
    var focused = document.activeElement;
    if (focused && focused.hasAttribute('data-search-result')) {
      if (currentQuery) saveRecent(currentQuery);
      return;
    }

    if (document.activeElement === input && currentQuery) {
      e.preventDefault();
      saveRecent(currentQuery);
      window.location.href = '/search?q=' + encodeURIComponent(currentQuery);
    }
  }

  /* ——— RECENT SEARCH EVENTS ——— */
  function onRecentClick(e) {
    var removeBtn = e.target.closest('[data-remove-recent]');
    if (removeBtn) {
      e.preventDefault();
      e.stopPropagation();
      var term = removeBtn.getAttribute('data-remove-recent');
      removeRecent(term);
      var recent = getRecent();
      if (recent.length > 0) {
        renderRecent(recent);
      } else {
        recentWrap.style.display = 'none';
      }
      return;
    }

    var item = e.target.closest('[data-recent-term]');
    if (item) {
      var term = item.getAttribute('data-recent-term');
      input.value = term;
      clearBtn.style.display = 'flex';
      doSearch(term);
    }
  }

  function onRecentClearClick() {
    clearAllRecent();
    recentWrap.style.display = 'none';
  }

  function onResultClick() {
    if (currentQuery) saveRecent(currentQuery);
    setTimeout(function () { closeModal(); }, 150);
  }

  function onTrendingClick(e) {
    var tag = e.target.closest('.search-trending-tag');
    if (tag) {
      e.preventDefault();
      var term = tag.textContent.trim();
      input.value = term;
      clearBtn.style.display = 'flex';
      doSearch(term);
    }
  }

  function onRetry() {
    if (currentQuery) doSearch(currentQuery);
    else showDefaultState();
  }

  /* ═══════════════════════════════════════════════════
     BIND EVENTS
     ═══════════════════════════════════════════════════ */
  function bindEvents() {
    /* Search trigger buttons */
    $$('[data-search-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        openModal();
      });
    });

    if (input)      input.addEventListener('input', onInputChange);
    if (clearBtn)   clearBtn.addEventListener('click', onClearClick);
    if (closeBtn)   closeBtn.addEventListener('click', closeModal);
    if (overlay)    overlay.addEventListener('click', onOverlayClick);
    if (recentList) recentList.addEventListener('click', onRecentClick);
    if (recentClear) recentClear.addEventListener('click', onRecentClearClick);
    if (trendingWrap) trendingWrap.addEventListener('click', onTrendingClick);

    if (resultsWrap) {
      resultsWrap.addEventListener('click', function (e) {
        if (e.target.closest('[data-search-result]')) onResultClick();
      });
    }

    if (viewAllBtn) {
      viewAllBtn.addEventListener('click', function () {
        if (currentQuery) saveRecent(currentQuery);
      });
    }

    if (viewProductsLink) {
      viewProductsLink.addEventListener('click', function () {
        if (currentQuery) saveRecent(currentQuery);
      });
    }

    if (retryBtn) {
      retryBtn.addEventListener('click', function (e) {
        e.preventDefault();
        onRetry();
      });
    }

    /* Focus trap */
    if (modal) {
      modal.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab') return;
        var focusable = $$('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])', modal);
        if (!focusable.length) return;

        var first = focusable[0];
        var last  = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
        }
      });
    }
  }

  /* ═══════════════════════════════════════════════════
     INIT
     ═══════════════════════════════════════════════════ */
  function init() {
    bindEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();